import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Loader from "./Loader";
import Spacer from "./Spacer";

const Wrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ circle }) => (circle ? "50%" : "8px")};
  height: ${({ height }) => (!height ? "48px" : height ?? "fit-content")};
  width: ${({ fullWidth, width }) =>
    fullWidth ? "100%" : width ?? "fit-content"};
  padding: 0 12px;
  background-color: ${({ bg }) => bg ?? "var(--secondary)"};
  color: ${({ color }) => (color ? color : "#ffffff")};
  border: ${({ bd }) => (bd ? `1px solid ${bd}` : null)};
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}` : "16px")};
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
  transition: var(--transition_default);

  .icon {
    margin-right: 4px;
    width: 16px;

    &:hover {
      color: #fff;
    }
  }

  .endIcon {
    margin-left: 12px;
    width: 16px;
    cursor: pointer;
  }

  &.delete {
    background-color: ${({ bg }) => bg ?? "var(--danger)"};
    color: ${({ color }) => color ?? "var(--white)"};

    &:not([disabled]):hover {
      background-color: #c80100;
      color: var(--white);
      border-color: #c80100;
    }

    &:disabled {
      background-color: var(--danger_disabled);
      color: var(--white);
    }

    &:active {
      background-color: #c80100;
      color: var(--white);
    }
  }

  &.bordered {
    border: 1px solid var(--secondary);
    color: var(--secondary);
    background-color: transparent;

    &:not([disabled]):hover {
      background-color: inherit;
      color: var(--secondary);
    }
  }

  &.borderedPrimary {
    border: 1px solid var(--primary);
    color: var(--primary);
    background-color: transparent;
  }

  &.borderedGrey {
    border: 1px solid var(--grey_10);
    color: var(--grey_1);
    background-color: transparent;

    &:not([disabled]):hover {
      background-color: var(--background);
      color: var(--grey_1);
      img {
        filter: ${({ invertOnHover }) =>
          invertOnHover ? `brightness(0) invert(0)` : ""};
      }
    }
  }

  &.dashedBorder {
    border: 1px dashed var(--grey_10);
    color: var(--grey_1);
    background-color: ${({ bg }) => bg ?? "transparent"};

    &:not([disabled]):hover {
      background-color: var(--background);
      color: var(--grey_1);
      img {
        filter: ${({ invertOnHover }) =>
          invertOnHover ? `brightness(0) invert(0)` : ""};
      }
    }
  }

  &.borderedDanger {
    border: 1px solid var(--danger);
    color: var(--danger);
    background-color: transparent;

    &:not([disabled]):hover {
      background-color: var(--danger);
      color: var(--white);
    }
  }

  &.noBorder {
    border: none;
    color: ${(props) => (props.color ? props.color : "var(--primary)")};
    background-color: transparent;

    &:not([disabled]):hover {
      background-color: inherit;
      color: ${(props) => (props.color ? props.color : "var(--primary)")};
    }
  }

  @media screen and (max-width: 768px) {
    font-size: 14px;
    line-height: 18px;
  }
`;

/**
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.bg]
 * @param {string} [props.type]
 * @param {boolean} [props.fullWidth]
 * @param {string} [props.width]
 * @param {string} [props.height]
 * @param {string} props.text
 * @param {boolean} [props.disabled]
 * @param {string} [props.color]
 * @param {string} [props.bd]
 * @param {string} [props.as]
 * @param {string} [props.href]
 * @param {Function} [props.onClick]
 * @param {boolean} [props.loading]
 * @param {any} [props.invertOnHover]
 * @param {any} [props.fontSize]
 * @param {any} [props.iconComponent]
 * @param icon
 * @param endIcon
 *
 * @returns
 */

const Button = ({
  className,
  bg,
  type,
  height,
  fullWidth,
  width,
  text,
  disabled,
  color,
  icon,
  endIcon,
  bd,
  as,
  href,
  onClick,
  loading,
  invertOnHover,
  fontSize,
  iconComponent,
  ...restProps
}) => {
  const styleProps = {
    className,
    bg: loading ? "transparent" : bg,
    type,
    height,
    fullWidth,
    width,
    text,
    disabled,
    color,
    bd,
    as,
    href,
    onClick,
    invertOnHover,
    fontSize,
    ...restProps,
  };

  if (loading) {
    return (
      <Wrapper {...styleProps}>
        <Loader type="spinner" />
      </Wrapper>
    );
  }

  return (
    <Wrapper {...styleProps}>
      {typeof icon === "string" ? (
        <img src={icon} alt="icon" className="icon" />
      ) : (
        icon ?? iconComponent
      )}
      {iconComponent && text && <Spacer x={0.5} />}
      {text && <span>{text}</span>}
      {typeof endIcon === "string" ? (
        <img src={endIcon} alt="endIcon" className="endIcon" />
      ) : (
        endIcon
      )}
    </Wrapper>
  );
};

Button.propTypes = {
  text: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string,
  bg: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  fullWidth: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  href: PropTypes.string,
  onClick: PropTypes.func,
};

export default Button;
